import React from 'react';
import { PackagePointsComparison } from './ProductAndPricingTypes';
import { Text } from '@admitkard/ccl/Text';
import { Icon } from '@admitkard/ccl/Icon';
import { theme } from '@admitkard/ccl/style';

interface PackagePointsComparisonTableProps {
  tableData: PackagePointsComparison;
}

const styles = `
  .package-points_mobile-display {
    display: block;
  }
  .package-points_desktop-display {
    display: none;
  }
  .package-points_point-name {
    color: #222753;
    font-weight: 700;
  }
  .package-points_comparison-column-values {
    transform: rotate(-90deg);
    display: flex;
    align-items: center;
  }
  .package-points_comparison-column-container {
    height: 6.25rem;
    display: flex;
    justify-content: space-around;
    border-bottom: 1px solid #DDDDDD;
  }
  .package-points-parameter-container {
    padding: ${theme.spacingOld(5)} 0;
    border-bottom: 1px solid #DDDDDD;
  }
  .package-points_parameter-description {
    margin: ${theme.spacingOld(5)} 0 ${theme.spacingOld(7)};
    line-height: 1.5;
  }
  .package-points_parameter-icon-container {
    display: flex;
    justify-content: space-around;
  }
  @media only screen and (min-width: 768px) {
    .package-points_mobile-display {
      display: none;
    }
    .package-points_desktop-display {
      display: block;
    }
    .package-points_desktop-display table {
      width: 100%;
      border-collapse: collapse;
    }
    .package-points_desktop-display table th {
      height: 5rem;
      border-right: 1px solid #DDDDDD;
      border-bottom: 1px solid #DDDDDD;
    }
    .package-points_desktop-display table th:first-child {
      text-align: left;
      padding-left: ${theme.spacingOld(7)};
    }
    .package-points_desktop-display table th:last-child {
      border-right: none;
    }
    .package-points_desktop-display table tr td{
      border-right: 1px solid #DDDDDD;
    }
    .package-points_desktop-display table tr td:last-child {
      border-right: none;
    }
    .package-points_desktop-display table td:first-child {
      width: 45%;
      padding-left: ${theme.spacingOld(7)};
      padding-right: ${theme.spacingOld(7)};
    }
    .package-points_desktop-display table tr:first-child td {
      padding-top: ${theme.spacingOld(7)};
    }
    .package-points_desktop-display table tr:last-child td {
      border-bottom: 1px solid #DDDDDD;
    }
    .package-points_td-center-align {
      text-align: center;
    }
    .package-points_desktop-point-name {
      color: #222753;
      font-size: 2.25rem;
      font-weight: 700;
    }
    .package-points_desktop-parameter-description {
      margin-top: ${theme.spacingOld(5)};
      margin-bottom: ${theme.spacingOld(6)};
    }
  }
`;

const PackagePointsComparisonTable: React.FunctionComponent<PackagePointsComparisonTableProps> = (props) => {
  const { tableData } = props;
  const mobileComparisonColumnValues = tableData.pointComparisonColumns.map((comparisonValue, index) => {
    return <Text variant='s' weight='bold' className="package-points_comparison-column-values" key={index}>
      {comparisonValue}
    </Text>;
  });
  const mobileComparisonRowValues = tableData.pointComparisonRows.map((comparisonParameter, index) => {
    return <div key={index} className="package-points-parameter-container">
      <Text weight='bold'>
        {comparisonParameter.rowHeading}
      </Text>
      <Text variant='s' lightness={8} className="package-points_parameter-description">
        {comparisonParameter.rowDescription}
      </Text>
      <div className="package-points_parameter-icon-container">
        {comparisonParameter.comparisonValues.map((comparisonValue, indexValue) => {
          return <Icon name={comparisonValue === 'no' ? 'cross' : 'circle_checkmark'} size='large' key={indexValue} />;
        })}
      </div>
    </div>;
  });
  const desktopComparisonHeading = tableData.pointComparisonColumns.map((comparisonValue, index) => {
    return <th key={index}>
      <Text weight='bold'>{comparisonValue}</Text>
    </th>;
  });
  const desktopComparisonRows = tableData.pointComparisonRows.map((comparisonParameter, index) => {
    return <tr key={index}>
      <td>
        <div>
          <Text weight='bold' display='block'>{comparisonParameter.rowHeading}</Text>
          <Text variant='s' lightness={8} className="package-points_desktop-parameter-description">
            {comparisonParameter.rowDescription}
          </Text>
        </div>
      </td>
      {comparisonParameter.comparisonValues.map((comparisonValue, indexValue) => {
        return <td key={indexValue} className="package-points_td-center-align">
          <Icon name={comparisonValue === 'no' ? 'cross' : 'circle_checkmark'} size='large' key={indexValue} />
        </td>;
      })}
    </tr>;
  });
  return <div>
    <div className="package-points_mobile-display">
      <Text variant='l' weight='bold' className="package-points_point-name" display='block'>
        {tableData.pointName}
      </Text>
      <div className="package-points_comparison-column-container">
        {mobileComparisonColumnValues}
      </div>
      <>
        {mobileComparisonRowValues}
      </>
    </div>
    <div className="package-points_desktop-display">
      <table>
        <thead>
          <th>
            <Text weight='bold' className="package-points_desktop-point-name">
              {tableData.pointName}
            </Text>
          </th>
          {desktopComparisonHeading}
        </thead>
        <tbody>
          {desktopComparisonRows}
        </tbody>
      </table>
    </div>
    <style>{styles}</style>
  </div>;
};

export default PackagePointsComparisonTable;
